const $ = window.jQuery;    

// Set global vars
let loadingSpinners = {
    'non-blocking': {},
    'page-blocking': {},
};
const notificationSpinners = {};

/** 
 * Logs messages to the console if debug mode is enabled.
 * 
 * Usage: log('Debug message', { key: 'value' });
 * 
 * Ensure that `window.ironman.debug` is set to true to enable logging.
 */
export function log(...args) {
    if (!(window.ironman && window.ironman.debug)) return;
  
    // 1. Create a dummy Error to get its stack
    const err = new Error();
  
    // 2. Split stack lines and pick the *second* entry, which is the caller
    //    ‣ stack[0] is “Error”
    //    ‣ stack[1] is inside this log() function
    //    ‣ stack[2] is the site that called log()
    const stackLines = err.stack?.split('\n') || [];
    const callerLine = (stackLines[2] || '').trim();
  
    // 3. Print with the IRONLOG prefix and the caller info
    console.log(`IRONLOG:`, ...args, `Caller: ${callerLine}`);
}
  

/**
 * Rounds a number to a specified number of decimal places.
 *
 * @param {number} value - The number to round.
 * @param {number} [precision=5] - The number of decimal places to keep (default is 5).
 * @returns {number} The rounded number.
 */
export function roundDecimal(value, precision = 5) {
    return Number(value.toFixed(precision));
}

//Fix for js floating point weirdness
export function round2(x) {
    return Math.round(x * 100) / 100;
}
export function round1(x) {
    return Math.round(x * 10) / 10;
}
export function round3(x) {
    return Math.round(x * 1000) / 1000;
}
export function round0(x) {
    return Math.round(x);
}

/**
 * Looks up a value in the given key of object or array.
 * @param {Object|Array} obj - The object or array to search.
 * @param {string} key - The key to search in.
 * @param {string} [value] - The value to find in the given key.
 * @return {any} The id where the value was found in the given key, otherwise false.
 */
export function searchObject(obj, key, value) {
    if (Array.isArray(obj)) {
        return obj.findIndex(item => item[key] === value);
    } else if (typeof obj === 'object') {
        return Object.keys(obj).find(k => obj[k][key] === value) || false;
    }
    return false;
}

/**
 * Checks if a key exists in the given source.
 * @param {Object|Map|URLSearchParams} source - The source to check.
 * @param {string} key - The key to check for.
 * @return {boolean} True if the key exists, otherwise false.
 * Basically, works like .has and .hasOwnProperty, but works for Maps, URLSearchParams, and objects.
 */
export function hasKey(source, key) {
    if (source instanceof URLSearchParams || source instanceof Map) {
        return source.has(key);
    } else if (typeof source === 'object' && source !== null) {
        return Object.prototype.hasOwnProperty.call(source, key);
    }
    return false;
}

/**
 * Gets the value associated with a key in the given source.
 * @param {Object|Map|URLSearchParams} source - The source to get the value from.
 * @param {string} key - The key to get the value for.
 * @return {any} The value associated with the key, or undefined if not found.
 * This works for Maps, URLSearchParams, and plain objects.
 */
export function getValue(source, key) {
    if (source instanceof URLSearchParams || source instanceof Map) {
      return source.get(key);
    } else if (typeof source === 'object' && source !== null) {
      return source[key]; // works for plain objects and arrays
    }
    return undefined;
}
  

export function normalizeString(str) {
    if(str) {
        return str.toLowerCase().trim();
    }
    return str;
}
export function removeAccents(str) {
    if(str) {
        return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    }
    return str;
}

/**
 * Deep freezes an object.
 * @param {Object} obj - The object to deep freeze.
 * @returns {Object} The deep frozen object.
 */
export function deepFreeze(obj) {
    Object.getOwnPropertyNames(obj).forEach(function(name) {
        let prop = obj[name];
        if (typeof prop === 'object' && prop !== null && !Object.isFrozen(prop)) {
            deepFreeze(prop);
        }
    });
    return Object.freeze(obj);
}

export function getBearing(lat1, lon1, lat2, lon2) {
    // Convert degrees to radians
    const toRadians = deg => deg * (Math.PI / 180);
    const toDegrees = rad => rad * (180 / Math.PI);
    
    lat1 = toRadians(lat1);
    lon1 = toRadians(lon1);
    lat2 = toRadians(lat2);
    lon2 = toRadians(lon2);
    
    const dLon = lon2 - lon1;
    
    const x = Math.sin(dLon) * Math.cos(lat2);
    const y = Math.cos(lat1) * Math.sin(lat2) -
                Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
    
    let bearing = Math.atan2(x, y);
    bearing = toDegrees(bearing);
    return (bearing + 360) % 360;  // Normalize to 0–360°
}

/**
 * Updates the loading spinner state for the given spinnerSlug.
 * Allows for multiple loading spinner slugs and if any are true, show the loading spinner.
 * @param {string} spinnerSlug - The slug of the spinner to update.
 * @param {boolean} showSpinner - Whether to show the spinner.
 * NOTE: Currently used either directly, via callAdminAjax(), or via fetchAPIDataFromAPI().
 * @param {string} spinnerType - The type of spinner to update. If not provided, no spinner will be shown.
 * @param {string} message - Can be either for use as the spinner message (when creating the spinner), or for use as an error message (when removing the spinner).
 */
export function updateLoadingSpinner(spinnerSlug, showSpinner, spinnerType=null, message=null) {
    log('updateLoadingSpinner: ', spinnerSlug, showSpinner, spinnerType);
    log('loadingSpinners: ', loadingSpinners);

    // Update the state in the global loadingSpinners object for the given spinnerSlug.
    if(showSpinner === true) {
        if(spinnerType !== 'page-blocking' && spinnerType !== 'non-blocking') {
            return;
        }
        // Check if spinner already exists.
        if(loadingSpinners[spinnerType][spinnerSlug]) {
            log('Spinner already exists: ', spinnerSlug);
            return;
        }

        // CREATE SPINNER
        loadingSpinners[spinnerType][spinnerSlug] = true;
        
        // Use spinner slug as message if no message is provided.
        message = message ?? spinnerSlug;
    
        // SHOW PAGE-BLOCKING SPINNER
        if(spinnerType === 'page-blocking') {
            $('#page-blocking-spinner').find('#loading-events').append(`<li data-spinner-slug="${spinnerSlug}">${message}<span class="error-message d-none text-danger ps-2"></span></li>`);
        } else if(spinnerType === 'non-blocking') {
            // SHOW NON-BLOCKING SPINNER
            showNotificationSpinner(spinnerSlug, true, message);
        }
    } else {
        // REMOVE SPINNER
        // Remove the spinner from the global loadingSpinners object.
        // Search for the spinner in all types of loadingSpinners object.
        if(hasKey(loadingSpinners['page-blocking'], spinnerSlug)) {
            loadingSpinners['page-blocking'][spinnerSlug] = false;
        } else if(hasKey(loadingSpinners['non-blocking'], spinnerSlug)) {
            loadingSpinners['non-blocking'][spinnerSlug] = false;
        } else {
            return;
        }

        // Set success flag (in case of error, showSpinner is null)
        let success = true;
        if(showSpinner === null) {
            success = false;
        }
        
        if(spinnerType === 'page-blocking') {
            if(success) {
                $('#page-blocking-spinner').find('#loading-events').children(`li[data-spinner-slug="${spinnerSlug}"]`).addClass('finished');
            } else {
                $('#page-blocking-spinner').find('#loading-events').children(`li[data-spinner-slug="${spinnerSlug}"]`).addClass('failed');
                $('#page-blocking-spinner').find('#loading-events').children(`li[data-spinner-slug="${spinnerSlug}"]`).find('.error-message').removeClass('d-none').text(message);
            }
        } else if(spinnerType === 'non-blocking') {
            if(success) {
                showNotificationSpinner(spinnerSlug, false);
            } else {
                showNotificationSpinner(spinnerSlug, 'failed', message);
            }
        }
    }

    // If there are any loading spinners enabled, show the loading spinner. (Page Blocking only)
    if(Object.values(loadingSpinners['page-blocking']).some(Boolean)) {
            $('#page-blocking-spinner').removeClass('d-none');
    } else {
        $('#page-blocking-spinner').addClass('d-none');
        $('#page-blocking-spinner').find('#loading-events').empty();
    }
}
window.updateLoadingSpinner = updateLoadingSpinner;

/**
 * Requests an admin AJAX endpoint.
 * @param {string} action - The action to call.
 * @param {Object} data - The data to send to the endpoint.
 * @returns {Promise} A promise that resolves with the response from the endpoint.
 */
async function doAdminAjaxRequest(action, data={}) {
    // Add the action to the data.
    const payload = { action, ...data};

    try {
        const response = await $.ajax({
            url: '/wp-admin/admin-ajax.php',
            type: 'POST',
            dataType: 'json',
            data: payload
        });
        return response;
    } catch (error) {
        // log('Error calling admin AJAX:', error);
        throw error;
    }
}

/**
 * Calls an admin AJAX endpoint with loading spinner and callbacks.
 * @param {string} action - The action to call.
 * @param {Object} data - The data to send to the endpoint.
 * @param {string|null} spinnerSlug - The slug of the spinner to update. If not provided, no spinner will be shown.
 * @param {Object} callbacks - The callbacks to call.
 * @param {Function} callbacks.onSuccess - The callback to call if the request is successful.
 * @param {Function} callbacks.onFail - The callback to call if the request fails.
 * @param {Function} callbacks.onComplete - The callback to call when the request is complete.
 * @param {string('page-blocking'|'non-blocking')|null} spinnerType - The type of spinner to update. If not provided, no spinner will be shown.
 * 
 * @returns {Promise} A promise that resolves with the response from the endpoint.
 * 
 * 2 Ways to use:
 * A) Faux-Synchronous: (aka Fire and Forget) relies on the callbacks to handle the response.
 * B) Asynchronous: returns a promise that resolves with the response from the endpoint.
 * 
 * Synchronous Example:
 * callAdminAjax('action', { data: 'value' }, 'spinnerSlug', {
 *     onSuccess: (response) => {
 *         log('Success:', response);
 *     },
 *     onFail: (error) => {
 *         log('Error:', error);
 *     },
 *     onComplete: () => {
 *         log('Complete');
 *     }
 * });
 * Doesn't need to be called in a try/catch block OR ASYNC because the callbacks are self-contained and won't run until the promise is resolved.
 * 
 * Asynchronous Example:
 * const response = await callAdminAjax('action', { data: 'value' }, 'spinnerSlug');
 * return response.data;
 * Should be called in a try/catch block. And parent function needs to be ASYNC. But this way parent function can return based on the response.data.
 * Use this when you need to return the response.data to the parent function.
 */
export async function callAdminAjax(action, data={}, spinnerSlug = null, callbacks = {}, spinnerType = null, spinnerMessage = null) {
    
    if(callbacks === null) {
        callbacks = {}
    }
    const { onSuccess, onFail, onComplete } = callbacks;
    let spinnerUpdated = false;
    
    if(spinnerSlug) {
        updateLoadingSpinner(spinnerSlug, true, spinnerType, spinnerMessage);
    }

    let spinnerErrorMessage = null;

    try {
        const response = await doAdminAjaxRequest(action, data);
        
        // Call onSuccess callback if provided
        if (onSuccess && typeof onSuccess === 'function') {
            onSuccess(response);
        }

        // If the response is successful, update the loading spinner.
        if(response?.success) {
            if(spinnerSlug) {
                updateLoadingSpinner(spinnerSlug, false, spinnerType);
                spinnerUpdated = true;
            }
        } else {
            spinnerErrorMessage = response?.display_error_message ?? 'Operation failed.';
        }
        
        return response;
    } catch (error) {
        log('Error calling admin AJAX:', error);
        
        // Call onFail callback if provided
        if (onFail && typeof onFail === 'function') {
            onFail(error);
        }
        
        spinnerErrorMessage = 'Connection error.';
        
        throw error;
    } finally {
        // Call complete callback if provided
        if (onComplete && typeof onComplete === 'function') {
            onComplete();
        }

        // There was an error, so update the loading spinner to show that the request failed. showSpinner is 'failed'.
        if(!spinnerUpdated && spinnerSlug) {
            updateLoadingSpinner(spinnerSlug, 'failed', spinnerType, spinnerErrorMessage);
        }
    }
}

export function convertTemperature(celsius, units='celsius') {
    if(units === 'fahrenheit' || units === 'f') {
        return round0(celsius * 1.8 + 32);
    } else {
        return round0(celsius);
    }
}

export function convertCurrency(euros, currency='EUR') {
    let converted = euros;
    // TODO: Get live exchange rate from API.
    // if(currency === 'USD') {
    //     converted = euros * 1.10;
    // } else if(currency === 'GBP') {
    //     converted = euros * 0.85;
    // } else if(currency === 'CAD') {
    //     converted = euros * 1.45;
    // } else if(currency === 'AUD') {
    //     converted = euros * 1.55;
    // }
    return converted.toFixed(2);
}

// Handle displaying currency with symbol and decimal places via Intl.NumberFormat
export function displayCurrency(amount, currency='EUR', locale='en-US') {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(amount);
}

// Convert meters to feet if needed. Returns rounded number.
export function convertDistance(meters, units='metric') {
    if(units === 'imperial') {
        return round2(meters * 3.28084);
    } else {
        return round2(meters);
    }
}

// Convert and display miles/kilometers distance with units and locale
export function displayLongDistance(meters, units='metric', locale='en-US') {
    let distance = meters / 1000;
    let unit = 'kilometer';
    // Convert meters to miles or kilometers
    if(units === 'imperial') {
        distance = meters * 3.28084 / 5280;
        unit = 'mile';
    }

    const formatted = new Intl.NumberFormat(locale, {
        style: 'unit',
        unit: unit,
        unitDisplay: 'short', // 'short' → "mi"/'km', 'long' → "miles"/'kilometers",
        maximumFractionDigits: 1,
    }).format(distance);
    return formatted;
}

// Convert and display feet/meters distance with units and locale
export function displayShortDistance(meters, units = 'metric', locale = 'en-US') {
    const isImperial = units?.toLowerCase() === 'imperial';

    const value = isImperial
        ? meters * 3.28084 // Convert meters to feet
        : meters;

    const unit = isImperial ? 'foot' : 'meter';

    return new Intl.NumberFormat(locale, {
        style: 'unit',
        unit,
        unitDisplay: 'short',
        maximumFractionDigits: 0, // No need for decimals for short distances
    }).format(value);
}

// Determine whether to display long or short distance based on the distance and units
export function displayDistance(meters, units = 'metric', locale = 'en-US') {
    const isImperial = units?.toLowerCase() === 'imperial';
    if((isImperial && meters >= 1609.34) || (!isImperial && meters >= 1000)) { // 1 mile in meters
        return displayLongDistance(meters, units, locale);
    } else {
        return displayShortDistance(meters, units, locale);
    }
}

// Convert and display speed with units and locale
export function displaySpeed(meters, seconds, units = 'metric', locale = 'en-US') {
    if (seconds === 0) return '0'; // Avoid divide-by-zero

    const isImperial = units?.toLowerCase() === 'imperial';
    const metersPerSecond = meters / seconds;

    // Convert to km/h or mph
    const speed = isImperial
        ? metersPerSecond * 2.23694
        : metersPerSecond * 3.6;

    const unit = isImperial ? 'mile-per-hour' : 'kilometer-per-hour';

    return new Intl.NumberFormat(locale, {
        style: 'unit',
        unit,
        unitDisplay: 'short',
        maximumFractionDigits: 1,
    }).format(speed);
}

// Convert and display temperature with units and locale
export function displayTemperature(celsius, units = 'metric', locale = 'en-US') {
    const isImperial = units?.toLowerCase() === 'f' || units?.toLowerCase() === 'fahrenheit';

    const value = isImperial
        ? (celsius * 9) / 5 + 32
        : celsius;

    const unit = isImperial ? 'fahrenheit' : 'celsius';

    return new Intl.NumberFormat(locale, {
        style: 'unit',
        unit,
        unitDisplay: 'narrow', // gives "°C" or "°F"
        maximumFractionDigits: 1,
    }).format(value);
}

export function displayElapsedTime(seconds, locale = 'en-US') {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    // seconds = seconds % 60;
    return new Intl.NumberFormat(locale, {
        style: 'unit',
        unit: 'hour',
        maximumFractionDigits: 0,
    }).format(hours) + ' ' + new Intl.NumberFormat(locale, {
        style: 'unit',
        unit: 'minute',
        maximumFractionDigits: 0,
    }).format(minutes);
}

/**
 * Estimate steps from distance, time, ascent, descent, height, gender, and backpack weight.
 * 
 * @param {number} distance_m - Total distance in meters.
 * @param {number} time_min - Total time in minutes.
 * @param {number} ascent_m - Total ascent in meters.
 * @param {number} descent_m - Total descent in meters.
 * @param {number} height_cm - Average human height in centimeters.
 * @param {string} gender - "male" or "female".
 * @param {number} backpack_kg - Optional backpack weight in kilograms.
 * @returns {number} Estimated number of steps.
 * Example:
 * console.log(estimateSteps(5000, 60, 200, 200, 170, "female", 10));
 * → ~7200 depending on tuning
 */
export function estimateSteps(distance_m, time_min, ascent_m, descent_m, height_cm, gender = "male", backpack_kg = 10) {
    // ================== Tunables (sitewide) ==================
    const TUNING_MULTIPLIER = 1.00;     // bump globally, e.g. 1.10 or 0.95
    const ASSUMED_UP_GRADE  = 0.08;     // 8% average uphill grade
    const ASSUMED_DN_GRADE  = 0.08;     // 8% average downhill grade
    const UP_STRIDE_FACTOR  = 0.90;     // uphill stride ~10% shorter
    const DN_STRIDE_FACTOR  = 1.04;     // downhill stride ~4% longer
    const LOAD_PENALTY_PER_KG = 0.003;  // 0.3% stride reduction per kg carried (10kg ≈ 3%)
    const MIN_STRIDE_M      = 0.35;     // safety floor
    const MAX_STRIDE_M      = 1.20;     // safety cap
    // =========================================================

    // Guards
    distance_m = Math.max(0, distance_m);
    ascent_m   = Math.max(0, ascent_m);
    descent_m  = Math.max(0, descent_m);
    const height_m = Math.max(0.5, height_cm / 100);

    // Base stride by gender
    const k = (gender.toLowerCase() === "female") ? 0.413 : 0.415;
    let base_stride = height_m * k;

    // Pace-based nudge (optional)
    if (time_min && time_min > 0) {
        const pace_mps = distance_m / (time_min * 60.0);
        if (pace_mps < 0.9)       base_stride *= 0.97;
        else if (pace_mps > 1.7)  base_stride *= 1.03;
    }

    // Backpack penalty (caps at 15%)
    const load_penalty = Math.min(0.15, Math.max(0, backpack_kg * LOAD_PENALTY_PER_KG));
    base_stride *= (1.0 - load_penalty);

    // Clamp stride
    base_stride = Math.min(MAX_STRIDE_M, Math.max(MIN_STRIDE_M, base_stride));

    // Convert ascent/descent into distances via assumed grade
    let uphill_dist   = (ASSUMED_UP_GRADE > 0) ? (ascent_m  / ASSUMED_UP_GRADE) : 0;
    let downhill_dist = (ASSUMED_DN_GRADE > 0) ? (descent_m / ASSUMED_DN_GRADE) : 0;

    // If segments exceed distance, scale proportionally
    let seg_sum = uphill_dist + downhill_dist;
    if (seg_sum > distance_m && seg_sum > 0) {
        const scale = distance_m / seg_sum;
        uphill_dist   *= scale;
        downhill_dist *= scale;
    }

    const flat_dist = Math.max(0, distance_m - uphill_dist - downhill_dist);

    // Stride modifiers
    let stride_up   = Math.min(MAX_STRIDE_M, Math.max(MIN_STRIDE_M, base_stride * UP_STRIDE_FACTOR));
    let stride_dn   = Math.min(MAX_STRIDE_M, Math.max(MIN_STRIDE_M, base_stride * DN_STRIDE_FACTOR));
    let stride_flat = base_stride;

    // Steps per segment
    const steps_up   = (uphill_dist   > 0) ? uphill_dist   / stride_up   : 0;
    const steps_dn   = (downhill_dist > 0) ? downhill_dist / stride_dn   : 0;
    const steps_flat = (flat_dist     > 0) ? flat_dist     / stride_flat : 0;

    const steps_total = (steps_up + steps_dn + steps_flat) * TUNING_MULTIPLIER;

    return Math.round(steps_total);
}
window.estimateSteps = estimateSteps;

/**
 * Sanitize a string for use as a WordPress-style post title.
 * Supports foreign languages (Unicode), trims, normalizes, and enforces length.
 *
 * @param {string} input       - The raw input title.
 * @param {number} minLength   - Minimum length (after sanitization).
 * @param {number} maxLength   - Maximum allowed length (string will be trimmed to this).
 * @param {boolean} allowEmoji - Whether to allow emoji and general symbols.
 * @returns {string|null|false} The sanitized title, or null if invalid, or false if too short.
 */
export function sanitizeWpTitle(input, minLength=3, maxLength=100, allowEmoji=false) {
    if (typeof input !== 'string') {
      return null;
    }
  
    // Trim and collapse whitespace
    let title = input.trim().replace(/\s+/g, ' ');
  
    // Normalize Unicode (important for consistency)
    title = title.normalize('NFC');
  
    // Remove disallowed characters
    const disallowed = allowEmoji
      ? /[^\p{L}\p{N}\p{S}\s.,!?:;'"\-–—_()]/gu
      : /[^\p{L}\p{N}\s.,!?:;'"\-–—_()]/gu;
    title = title.replace(disallowed, '');
  
    // Re-collapse any leftover spaces
    title = title.replace(/\s+/g, ' ').trim();
  
    // Enforce max length
    if (typeof maxLength === 'number' && maxLength > 0) {
      title = title.slice(0, maxLength).trim();
    }
  
    // Validate min length
    if (typeof minLength === 'number' && title.length < minLength) {
      return false;
    }
  
    return title;
  }
  
  export function getBootstrap() {
    if(window.bootstrap) {
        return window.bootstrap;
    } else {
        return null;
    }
  }

/**
 * Get a template element by ID and clone it.
 * @param {string} id 
 * @returns {jQuery} The cloned template element content.
 * 
 * USAGE NOTE: This REQUIRES a SINGLE root element in the template.
 * In other words, the template must have a single root element that is the parent of all the content.
 * If the template has multiple root elements, only the first one will be used.
 */
export function getTemplate(id) {
    const el = document.getElementById(id);
    if(!el) {
        log('getTemplate: Template not found', id);
        return $(); // Returns an empty jQuery object
    }
    const clone = el.content.cloneNode(true);
    
    // This is a DOM Element: <div class="place-popup">
    const firstChild = clone.firstElementChild; 
    
    // This returns a JQUERY OBJECT containing that element
    return $(firstChild);
}

export function nextFrame() {
    return new Promise(resolve => requestAnimationFrame(resolve));
}
export function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export function desktopMediaQuery() {
    return window.matchMedia('(min-width: 768px)');
}
export function isDesktop() {
    return desktopMediaQuery().matches;
}
export function isMobile() {
    return !isDesktop();
}

/**
 * Split the media gallery by type.
 * @param {array} mediaGallery - The media gallery.
 * @returns {object} The media gallery, split by type.
 */
export function splitMediaGalleryByType(mediaGallery) {
    // Count stage media items
    const stageImages = [];
    const stageVideos = [];
    mediaGallery.forEach((media) => {
        if(media.media_type === 'image') {
            stageImages.push(media);
        } else if(media.media_type === 'video') {
            stageVideos.push(media);
        }
    });
    return {
        stageImages: stageImages,
        stageVideos: stageVideos,
    };
}

/**
 * Get the swiper items for the media items.
 * @param {array} mediaItems - The media items.
 * @param {jQuery} swiperItemTemplate - The swiper item template.
 * @returns {array} The swiper items.
 * 
 * Note: this is used with different templates for different purposes, but the finds are non-destructive so they can play nicely.
 */
export function getSwiperMediaItems(mediaItems, swiperItemTemplate) {
    const swiperItems = [];
    mediaItems.forEach((media) => {
        const item = swiperItemTemplate.clone();

        // Single share image replacements:
        item.find('img').attr('src', media.media_url_thumbnail);
        item.find('input').attr('id', 'media-item-'+media.id);
        item.find('input').attr('name', 'media-image');
        item.find('label').attr('for', 'media-item-'+media.id);
        item.find('input').attr('value', media.id);

        // Directory root image replacements:
        item.find('img').attr('alt', media.media_caption);
        item.find('a').attr('data-index', media.media_index);

        swiperItems.push(item.get(0));
    });
    return swiperItems;
}

/**
 * Remove an item from an array.
 * @param {array} array - The array to remove the item from.
 * @param {any} item - The item to remove from the array.
 * @returns {array} The array with the item removed.
 * Note: this is a destructive operation. Changes the original array.
 */
export function removeItemFromArray(array, item) {
    if(!array || !item) {
        return array;
    }
    const index = array.indexOf(item);
    if(index > -1) {
        array.splice(index, 1);
    }
    return array;
}
export function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

export function textToParagraphs(text) {
    if(!text) {
        return '';
    }
    return '<p>' +
      text
        .trim()
        .split(/\n\s*\n/) // split on blank lines
        .map(p => p.replace(/\n/g, '<br>')) // optional single line breaks
        .join('</p><p>') +
      '</p>';
}

/**
 * Show a notification spinner.
 * @param {string} id - The id of the spinner.
 * @param {boolean} showSpinner - Whether the spinner is loading.
 * @param {string} text - The text to display in the spinner.
 * @returns {void}
 */
function showNotificationSpinner(id, showSpinner, message=null) {
    let endingClass = '';

    // START
    if (showSpinner === true) {

        // already exists
        if (notificationSpinners[id]) {
            return;
        }

        // Use message if provided, otherwise use default message.
        if(!message) {
            message = 'Loading...';
        }

        const $spinner = $(`
        <div class="notification-spinner" data-id="${id}">
            <div class="spinner-border spinner-border-sm"></div>

            <div class="notification-spinner-success">
                <i class="cid-check-fat"></i>
            </div>
            <div class="notification-spinner-fail">
                <i class="cid-x-circle"></i>
            </div>

            <div class="notification-text">
            ${message}
            </div>
            <div class="error-message d-none"></div>
        </div>
        `);

        $("#notificationSpinners").append($spinner);

        notificationSpinners[id] = $spinner;

        // trigger fade-in transition
        requestAnimationFrame(() => {
            $spinner.addClass("show");
        });

        return;
    } else if (showSpinner === 'failed') {
        // FAILED
        endingClass = 'failed';
        if(!message) {
            message = 'Failed to load data.';
        }
    } else {
        // SUCCESS
        endingClass = 'finished';
    }

    // Get spinner element.
    const $spinner = notificationSpinners?.hasOwnProperty(id) ? notificationSpinners[id] : null;
    if (!$spinner) {
        return;
    }
    
    // Set error message if failed.
    if(endingClass === 'failed') {
        $spinner.find('.error-message').removeClass('d-none').text(message);
    }
    
    // Set ending class.
    $spinner.addClass(endingClass);

    // Remove spinner after animation.
    setTimeout(() => {
        $spinner.addClass("removing");
        $spinner.on("transitionend", function () {
            $spinner.remove();
            delete notificationSpinners[id];
        });
    }, 1000);
}

export function isValidDateString(dateString) {
    if(!dateString || dateString.length !== 10) {
        return false;
    }
    const datePieces = dateString.split('-');
    if(datePieces.length !== 3) {
        return false;
    }
    const year = parseInt(datePieces[0]);
    const month = parseInt(datePieces[1]);
    const day = parseInt(datePieces[2]);
    if(year < 1900 || year > 2100 || month < 1 || month > 12 || day < 1 || day > 31) {
        return false;
    }

    return true;
}

export function isValidTransport(transport) {
    if(!transport || !ironman.appConfig.modesOfTransport) {
        return false;
    }
    if(!Object.keys(ironman.appConfig.modesOfTransport).includes(transport)) {
        return false;
    }
    return true; // If we get here, the transport is valid.
}


////////////////////////////////////////////////////////////
// String Translation Functions
////////////////////////////////////////////////////////////
// Usage: Either call getTranslatedString() directly, or use createTranslator() to create a translation function permanently bound to one or more namespaces.

/**
 * Replace placeholders such as:
 *
 * "Welcome, {name}!"
 *
 * with values from:
 *
 * { name: "Steve" }
 */
function interpolateString(value, replacements) {
    return value.replace(
        /\{([a-zA-Z0-9_]+)\}/g,
        (placeholder, name) => {
            if (!Object.hasOwn(replacements, name)) {
                return placeholder;
            }

            return String(replacements[name]);
        }
    );
}

/**
 * Retrieve a translated string from a namespace.
 *
 * @param {string|array} namespace - Can be a string or an array of strings.
 * @param {string} key
 * @param {Record<string, unknown>} replacements
 * @returns {string}
 */
export function getTranslatedString(
    namespace,
    key,
    replacements = {}
) {
    const catalog = window.ironman.translate ?? {};
    
    // Convert provided namespace to an array if it is a string.
    if(typeof namespace === 'string') {
        namespace = [namespace];
    }

    // Add common to the end of the namespace array.
    if(!namespace.includes('common')) {
        namespace.push('common');
    }

    // Loop through the namespaces and check if the key exists in the catalog. If it does, return the value.
    for(const ns of namespace) {
        const value = catalog[ns]?.[key];
        if(value) {
            return interpolateString(value, replacements);
        }
    }
    return key; // If we get here, the key was not found in any of the namespaces, so return the key as is.
}

/**
 * Create a translation function permanently bound to one namespace.
 *
 * @param {string|array} namespace - Can be a string or an array of strings.
 * @returns {(key: string, replacements?: Record<string, unknown>) => string}
 */
export function createTranslator(namespace) {
    return (key, replacements = {}) =>
        getTranslatedString(
            namespace,
            key,
            replacements
        );
}

////////////////////////////////////////////////////////////

/**
 * Searches primary route children for a matching child-route slug and returns the name of the child route.
 * @param {string} inputChildRouteSlug
 * @returns {string|false}
 */
export function childRouteNameLookup(inputChildRouteSlug) {
    const routeTaxonomy = window.ironman?.appConfig?.routeTaxonomy;
    if (!routeTaxonomy) {
        return false;
    }
    for (const routeData of Object.values(routeTaxonomy)) {
        if (!routeData.children) {
            continue;
        }
        const childRoute = routeData.children[inputChildRouteSlug];
        if (childRoute) {
            return childRoute.name;
        }
    }
    return false;
}